/**
 * The collision prompt of one job (main publishes one `pendingCollision` at a time). UI/UX fix round A (reviews A3 /
 * U4 / A18): a way out — Cancel upload / download, also on Escape, with an inline error when the cancel fails; no
 * destructive default — Skip is the primary button and has the initial focus, Overwrite is a destructive outline;
 * "Keep both" (never "Rename": nothing is typed) explains the numbered name; and "Do the same for the other conflicts
 * in this upload" answers the job's next prompts with the same choice where they offer it. Final UI/UX batch C1 (N28):
 * the existing file and the one being transferred are compared (size, last modified) when main knows them.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { ReasonButton } from '../../../components/ui/ReasonButton';
import { NFS_OVERWRITE_REASON } from '../../../core/engines/azureStorageFileCoverage';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import type { TransferCollisionSide, TransferJob } from '../../../types/azureStorageTransfers';
import { formatStorageDateTime, storageDateTimeUtc } from '../../workspace/storage-explorer/utils/storageDateTime';
import type { CollisionDecisionMeta } from '../hooks/useTransferActions';
import { trackTransferEvent } from '../services/transfersApi';
import { collisionDestinationLabel, formatTransferBytes } from '../model/transferJobRules';
import { collisionCancelLabel, collisionOffers, keepBothExample, keepBothFirstNumber, type CollisionAction } from '../model/collisionRules';

type TransferCollisionDialogProps = {
  job?: TransferJob;
  onDecide: (jobId: string, item: string, decision: CollisionAction, destination?: string, meta?: CollisionDecisionMeta) => Promise<boolean>;
  onCancelJob: (jobId: string, via: 'button' | 'escape') => Promise<{ ok: true } | { ok: false; message: string }>;
};

/** C1 (N28): the file already there beside the one being transferred — size and last modified, each when known. */
function CollisionComparison({ existing, incoming, incomingLabel }: { existing?: TransferCollisionSide; incoming?: TransferCollisionSide; incomingLabel: string }) {
  const text = (side?: TransferCollisionSide): string => [side?.size !== undefined ? formatTransferBytes(side.size) : undefined, side?.lastModified ? formatStorageDateTime(side.lastModified) : undefined].filter(Boolean).join(' · ') || 'not known';
  return (
    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 rounded-md border border-border bg-muted/30 p-2 text-xs" data-testid="transfer-collision-compare">
      <dt className="text-muted-foreground">Existing</dt><dd className="tabular-nums" title={existing?.lastModified ? storageDateTimeUtc(existing.lastModified) : undefined} data-testid="transfer-collision-existing">{text(existing)}</dd>
      <dt className="text-muted-foreground">{incomingLabel}</dt><dd className="tabular-nums" title={incoming?.lastModified ? storageDateTimeUtc(incoming.lastModified) : undefined} data-testid="transfer-collision-incoming">{text(incoming)}</dd>
    </dl>
  );
}

export function TransferCollisionDialog({ job, onDecide, onCancelJob }: TransferCollisionDialogProps) {
  // Scoped to the collision it was set for (review B M11): an accepted decision stays "submitting" only until main
  // publishes the NEXT collision of the same job, which must open with every choice enabled.
  const [submittingFor, setSubmittingFor] = useState<string>();
  const [applyToRest, setApplyToRest] = useState(false);
  const [cancelError, setCancelError] = useState<string>();
  const skipRef = useRef<HTMLButtonElement>(null);
  const collision = job?.pendingCollision;
  const collisionKey = job && collision ? `${job.id}\u0000${collision.item}` : undefined;
  const submitting = submittingFor !== undefined && submittingFor === collisionKey;
  useEffect(() => {
    if (!job || !collision) return;
    trackTransferEvent('feature:storage_transfer_action', { action: 'collision_open', kind: job.kind, origin: job.origin, scope: job.scope });
  }, [job?.id, collision?.item]);
  if (!job || !collision) return null;

  const choose = async (decision: CollisionAction) => {
    if (submitting) return;
    const key = collisionKey;
    setSubmittingFor(key);
    const accepted = await onDecide(job.id, collision.item, decision, undefined, applyToRest ? { applyToRest: true } : {});
    if (!accepted) setSubmittingFor((current) => (current === key ? undefined : current));
  };
  const cancelJob = async (via: 'button' | 'escape') => {
    if (submitting) return;
    setCancelError(undefined);
    const result = await onCancelJob(job.id, via);
    if (!result.ok) setCancelError(result.message);
  };

  // Review D D5: another file of the same upload goes to this name — one item never overwrites another, so no Overwrite.
  const duplicate = collision.reason === 'duplicate-in-job';
  // Review D F5 (P3): an Overwrite the prompt does not offer — outside a duplicate-in-job prompt that is an NFS share,
  // the only S7 source of such a prompt — stays visible, disabled, with its reason in the body and as its tooltip.
  const overwriteReason = !duplicate && !collisionOffers(collision, 'overwrite') ? NFS_OVERWRITE_REASON : undefined;
  const label = collisionDestinationLabel(collision.destination);
  const noun = job.kind === 'download' ? 'download' : job.kind === 'upload' ? 'upload' : 'transfer';
  return (
    <Dialog open>
      <DialogContent
        data-testid="transfer-collision-dialog"
        onOpenAutoFocus={(event) => { event.preventDefault(); skipRef.current?.focus(); }}
        onEscapeKeyDown={(event) => { event.preventDefault(); void cancelJob('escape'); }}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{duplicate ? 'Duplicate destination name' : 'Destination already exists'}</DialogTitle>
          <DialogDescription className="break-words" data-testid="transfer-collision-destination">
            {duplicate ? `Another file in this ${noun} has the same destination name: ${label}.` : label}
          </DialogDescription>
        </DialogHeader>
        {!duplicate && (collision.existing || collision.incoming) && <CollisionComparison existing={collision.existing} incoming={collision.incoming} incomingLabel={job.kind === 'download' ? 'Downloading' : 'Uploading'} />}
        <ul className="space-y-1 text-xs text-muted-foreground" data-testid="transfer-collision-choices">
          {collisionOffers(collision, 'skip') && <li><span className="font-medium text-foreground">Skip</span> leaves {duplicate ? 'the other file' : 'the existing file'} and does not transfer this one.</li>}
          {collisionOffers(collision, 'rename') && <li data-testid="transfer-collision-keep-both-hint"><span className="font-medium text-foreground">Keep both</span> transfers this one under a numbered name, such as {keepBothExample(label, keepBothFirstNumber(job))}.</li>}
          {/* An Overwrite the prompt does not offer is not listed as a choice; its reason is said below instead. */}
          {!duplicate && !overwriteReason && <li><span className="font-medium text-foreground">Overwrite</span> replaces the existing file. It cannot be undone.</li>}
        </ul>
        {overwriteReason && <p className="text-xs text-muted-foreground" data-testid="transfer-collision-overwrite-reason">{overwriteReason}</p>}
        <label className="flex items-center gap-2 text-xs">
          <Checkbox checked={applyToRest} onCheckedChange={(value) => setApplyToRest(value === true)} disabled={submitting} data-testid="transfer-collision-apply-rest" />
          Do the same for the other conflicts in this {noun}
        </label>
        {cancelError && <p className="text-xs text-destructive" role="alert" data-testid="transfer-collision-cancel-error">Cancel failed: {cancelError}</p>}
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" disabled={submitting} onClick={() => void cancelJob('button')} data-testid="transfer-collision-cancel">{collisionCancelLabel(job.kind)}</Button>
          <div className="flex flex-wrap justify-end gap-2">
            {collisionOffers(collision, 'skip') && <Button ref={skipRef} disabled={submitting} onClick={() => void choose('skip')} data-testid="transfer-collision-skip">Skip</Button>}
            {collisionOffers(collision, 'rename') && <Button variant="outline" disabled={submitting} onClick={() => void choose('rename')} data-testid="transfer-collision-rename">Keep both</Button>}
            {!duplicate && <ReasonButton variant="outline" className="border-destructive text-destructive hover:bg-destructive/10" reason={overwriteReason} disabled={submitting} onClick={() => void choose('overwrite')} data-testid="transfer-collision-overwrite">Overwrite</ReasonButton>}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
